import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Phone, MessageCircle, MapPin, Check } from "lucide-react";
import { api, WHATSAPP_LINK } from "@/lib/config";

const FASCE = ["9.00 / 11.00", "11.00 / 13.00", "13.00 / 15.00", "15.00 / 17.00", "17.00 / 19.00"];

export default function ContactForm() {
  const [form, setForm] = useState({ nome: "", cognome: "", telefono: "", fascia_oraria: "", messaggio: "" });
  const [privacy, setPrivacy] = useState(false);
  const [letto, setLetto] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.telefono || !form.fascia_oraria) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    if (!privacy || !letto) {
      toast.error("Devi accettare l'informativa privacy per proseguire.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", { ...form, privacy });
      setSent(true);
      toast.success("Messaggio inviato! Ti richiameremo nella fascia oraria scelta.");
    } catch {
      toast.error("Errore nell'invio. Riprova o scrivici su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" className="py-24 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14">
        <div>
          <span className="text-brand-terracotta font-semibold tracking-[0.2em] uppercase text-sm">Contattaci</span>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-brand-ink mt-4 mb-6">
            Parliamo del tuo progetto
          </h2>
          <p className="text-brand-slate/70 text-lg mb-10 max-w-md">
            Lasciaci i tuoi dati e la fascia oraria in cui preferisci essere richiamato.
            Il primo sopralluogo è sempre gratuito e senza impegno.
          </p>

          <div className="space-y-5">
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 flex items-center justify-center group-hover:bg-[#25D366]/20 transition-colors">
                <MessageCircle className="text-[#25D366]" size={22} />
              </div>
              <div>
                <div className="text-brand-slate/50 text-sm">Scrivici su WhatsApp</div>
                <div className="font-display font-bold text-brand-ink">+39 379 354 5087</div>
              </div>
            </a>
            <a href="tel:+393793545087" data-testid="contact-phone-link" className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-xl bg-brand-ink/5 flex items-center justify-center group-hover:bg-brand-ink/10 transition-colors">
                <Phone className="text-brand-terracotta" size={22} />
              </div>
              <div>
                <div className="text-brand-slate/50 text-sm">Chiamaci</div>
                <div className="font-display font-bold text-brand-ink">+39 379 354 5087</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-brand-ink/5 flex items-center justify-center">
                <MapPin className="text-brand-terracotta" size={22} />
              </div>
              <div>
                <div className="text-brand-slate/50 text-sm">Zona operativa</div>
                <div className="font-display font-bold text-brand-ink">Napoli e provincia</div>
              </div>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-white rounded-2xl border border-gray-100 shadow-xl p-8 md:p-10"
        >
          {sent ? (
            <div data-testid="contact-success" className="h-full flex flex-col items-center justify-center text-center py-10">
              <div className="w-16 h-16 rounded-full bg-brand-sage/15 flex items-center justify-center mb-5">
                <Check className="text-brand-sage" size={32} />
              </div>
              <h3 className="font-display text-2xl font-bold text-brand-ink mb-2">Grazie!</h3>
              <p className="text-brand-slate/65">Abbiamo ricevuto la tua richiesta. Ti ricontatteremo al più presto.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-brand-slate mb-2">Nome *</label>
                  <Input data-testid="contact-nome" value={form.nome} onChange={(e) => upd("nome", e.target.value)} className="h-12" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-slate mb-2">Cognome *</label>
                  <Input data-testid="contact-cognome" value={form.cognome} onChange={(e) => upd("cognome", e.target.value)} className="h-12" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-brand-slate mb-2">Telefono *</label>
                <Input data-testid="contact-telefono" value={form.telefono} onChange={(e) => upd("telefono", e.target.value)} className="h-12" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-brand-slate mb-2">Quando possiamo richiamarti? *</label>
                <Select value={form.fascia_oraria} onValueChange={(v) => upd("fascia_oraria", v)}>
                  <SelectTrigger data-testid="contact-fascia" className="h-12">
                    <SelectValue placeholder="Seleziona una fascia oraria" />
                  </SelectTrigger>
                  <SelectContent>
                    {FASCE.map((f) => (
                      <SelectItem key={f} value={f} data-testid={`fascia-${f}`}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-brand-slate mb-2">Messaggio (opzionale)</label>
                <Textarea data-testid="contact-messaggio" value={form.messaggio} onChange={(e) => upd("messaggio", e.target.value)} rows={3} placeholder="Raccontaci brevemente il tuo progetto..." />
              </div>
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox data-testid="contact-privacy" checked={privacy} onCheckedChange={setPrivacy} className="mt-0.5" />
                  <span className="text-xs text-brand-slate/70 leading-relaxed">
                    Acconsento al trattamento dei dati personali ai sensi del Regolamento UE 2016/679, come descritto nella{" "}
                    <a href="/privacy" target="_blank" rel="noreferrer" data-testid="contact-privacy-link" className="text-brand-goldDark font-semibold underline">Privacy Policy</a>. *
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox data-testid="contact-letto" checked={letto} onCheckedChange={setLetto} className="mt-0.5" />
                  <span className="text-xs text-brand-slate/70 leading-relaxed">
                    Ho letto l'informativa{" "}
                    <a href="/privacy" target="_blank" rel="noreferrer" data-testid="contact-letto-link" className="text-brand-goldDark font-semibold underline">Privacy</a>. *
                  </span>
                </label>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="w-full bg-brand-gold hover:bg-brand-goldDark text-brand-ink font-bold py-4 rounded-full transition-colors disabled:opacity-60"
              >
                {loading ? "Invio in corso..." : "Contattatemi"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
